import { invokeEdgeFunctionSafely } from '@/lib/supabase-functions-wrapper';
import type { UserProfile, Role, UserStatus } from '@/lib/types/core';
import { componentLogger } from '@/lib/logging';

export async function fetchUserProfiles(): Promise<UserProfile[]> {
  // Use the get-users edge function instead of direct database query
  // This follows the security rule that all database operations must go through edge functions
  const { data: users, error } = await invokeEdgeFunctionSafely('get-users');

  if (error) {
    // Check if this is a cold start error that should be handled silently
    if ((error as { code?: string }).code === 'COLD_START_RETRIES_EXHAUSTED') {
      componentLogger.warn('Cold start retries exhausted for get-users', 'This might indicate a system issue');
      throw new Error('The system is starting up. Please refresh the page in a moment.');
    }

    componentLogger.error('Failed operation:', JSON.stringify(error));
    throw error;
  }

  if (!users || !Array.isArray(users)) {
    componentLogger.error('Invalid response from get-users function:', JSON.stringify(users));
    return [];
  }

  // Map the edge function response to UserProfile format
  return users.map((user: unknown) => {
    const typedUser = user as { id: string; created_at: string; email: string; first_name: string; last_name: string; phone_number?: string; language?: string; role: string; status: string; company_id?: string; company_name?: string; is_representative?: boolean; title?: string };
    return {
      id: typedUser.id,
      created_at: typedUser.created_at,
      email: typedUser.email,
      first_name: typedUser.first_name,
      last_name: typedUser.last_name,
      phone_number: typedUser.phone_number || null,
      language: typedUser.language || null,
      app_role: typedUser.role as Role,
      status: typedUser.status as UserStatus,
      company_id: typedUser.company_id,
      company_name: typedUser.company_name,
      is_representative: typedUser.is_representative || false,
      title: typedUser.title || null,
    } as UserProfile;
  });
}

export interface UpdateUserProfilePayload {
  first_name?: string;
  last_name?: string;
  email?: string; // Emails might be updatable, but often have special handling (e.g., re-verification)
  phone_number?: string;
  language?: string; // User preferred language (en, es, fr, sv)
  app_role?: Role; // Use the Role type from @/types
  company_id?: string | null;
  status?: UserStatus;
  is_representative?: boolean;
  title?: string;
  // Add other fields from your UserProfile type that are updatable by an admin/company_admin
}

export async function updateUserProfileOnServer(
  userIdToUpdate: string, 
  profileData: UpdateUserProfilePayload
): Promise<{ message: string; profile: UserProfile }> { 
  const { data, error } = await invokeEdgeFunctionSafely(
    'update-user-profile',
    {
      body: { userIdToUpdate, ...profileData },
    }
  );

  if (error) {
    componentLogger.error('Failed operation:', JSON.stringify(error));
    throw new Error((error as { message?: string }).message || 'Failed to update user profile.');
  }

  // The Edge function returns the updated profile object directly
  // We need to map this back to UserProfile if the structure is different (e.g., company object)
  if (!data) {
    componentLogger.error('Function returned an error or no profile:', JSON.stringify((data as { error?: unknown })?.error || null), JSON.stringify((data as { details?: unknown })?.details || null));
    const errorData = data as { error?: { message?: string } | string };
    const errorMessage = typeof errorData.error === 'string'
      ? errorData.error
      : errorData.error?.message || 'Function failed to update profile or returned no profile.';
    throw new Error(errorMessage);
  }
  
  // Assuming the edge function returns profile similar to how fetchUserProfiles gets it (with a nested company object)
  const rawProfile = data as { id: string; created_at: string; email: string; first_name: string; last_name: string; phone_number?: string; language?: string; role: string; status: string; company_id?: string; company?: { id: string; name: string } | { id: string; name: string }[] | null; is_representative?: boolean }; // Cast for mapping, then to UserProfile
  const companyData = rawProfile.company as { id: string; name: string } | { id: string; name: string }[] | null;
  let companyName: string | undefined | null = null;
  if (Array.isArray(companyData) && companyData.length > 0) {
    companyName = companyData[0]?.name;
  } else if (companyData && !Array.isArray(companyData)) {
    companyName = companyData.name;
  }

  const mappedProfile: UserProfile = {
    id: rawProfile.id,
    created_at: rawProfile.created_at,
    email: rawProfile.email || null,
    first_name: rawProfile.first_name || null,
    last_name: rawProfile.last_name || null,
    phone_number: rawProfile.phone_number || null,
    language: rawProfile.language || null,
    app_role: (rawProfile.role as Role) || null,
    status: (rawProfile.status as UserStatus) || null,
    company_id: rawProfile.company_id || null,
    company_name: companyName || null,
    is_representative: rawProfile.is_representative || null,
  };

  // The function doesn't return a message object anymore, just the profile
  // We can construct a standard success message here
  return { message: 'Profile updated successfully.', profile: mappedProfile };
}

// Mock data (can be removed or kept for testing offline)
// import { faker } from '@faker-js/faker';
// export const users_mock = Array.from({ length: 20 }, () => {
//   const firstName = faker.person.firstName();
//   const lastName = faker.person.lastName();
//   return {
//     id: faker.string.uuid(),
//     first_name: firstName,
//     last_name: lastName,
//     email: faker.internet.email({ firstName, lastName }).toLocaleLowerCase(),
//     phone_number: faker.phone.number(), 
//     role: faker.helpers.arrayElement(['USER', 'COMPANY_ADMIN', 'ADMIN']) as UserProfile['role'],
//     is_active: faker.datatype.boolean(),
//     company_id: faker.string.uuid(),
//     company_name: faker.company.name(),
//     created_at: faker.date.past().toISOString(),
//     updated_at: faker.date.recent().toISOString(),
//   } as UserProfile;
// });
